import { useMemo, useState, type FormEvent } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import { useAcquisitionChannels } from '@/hooks/channels/useAcquisitionChannels';
import { useCreateAcquisitionChannel } from '@/hooks/channels/useCreateAcquisitionChannel';
import { useUpdateAcquisitionChannel } from '@/hooks/channels/useUpdateAcquisitionChannel';
import { isApiError } from '@/lib/api/errors';
import type { AcquisitionChannel } from '@/types/acquisitionChannel';

/**
 * The 409 the API raises for a similar name is a question, not a failure: it
 * carries the names it matched so the tenant can say "yes, I meant both".
 * Anything else is an ordinary error and is reported as one.
 */
function similarNamesFrom(error: unknown): string[] | null {
  // `useApiMutation` normalizes every rejection through `normalizeError`, so
  // what lands here is an ApiError: status and the raw response body are
  // top-level. There is no `.response` to unwrap — reading one would silently
  // treat every 409 as an ordinary failure and never ask the question.
  if (!isApiError(error) || error.status !== 409) return null;
  const similar = (error.data as { similar?: unknown } | undefined)?.similar;
  if (!Array.isArray(similar)) return null;
  const names = similar.filter((name): name is string => typeof name === 'string');
  return names.length > 0 ? names : null;
}

function messageFrom(error: unknown, fallback: string): string {
  // `normalizeError` has already lifted the server's message — including the
  // string[] Nest's ValidationPipe emits — onto `message`.
  if (isApiError(error)) return error.message || fallback;
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : fallback;
}

function ChannelRow({
  channel,
  canConfigure,
}: {
  channel: AcquisitionChannel;
  canConfigure: boolean;
}) {
  const updateChannel = useUpdateAcquisitionChannel();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(channel.name);
  const [error, setError] = useState<string | null>(null);

  function startRename() {
    setName(channel.name);
    setError(null);
    setRenaming(true);
  }

  async function saveRename() {
    const trimmed = name.trim();
    if (!canConfigure || !trimmed || trimmed === channel.name) {
      setRenaming(false);
      return;
    }
    try {
      await updateChannel.mutateAsync({ id: channel.id, dto: { name: trimmed } });
      setRenaming(false);
      setError(null);
    } catch (caught) {
      setError(messageFrom(caught, 'That name could not be saved.'));
    }
  }

  // Archiving, never deleting: leads, contacts and timeline events reference
  // this row and must keep reading correctly. Archiving only decides what a
  // human is offered in a picker.
  async function toggleArchived() {
    if (!canConfigure) return;
    try {
      await updateChannel.mutateAsync({
        id: channel.id,
        dto: { isActive: !channel.isActive },
      });
      setError(null);
    } catch (caught) {
      setError(messageFrom(caught, 'That change could not be saved.'));
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {renaming ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Label htmlFor={`acquisition-channel-name-${channel.id}`} className="sr-only">
              New name for {channel.name}
            </Label>
            <Input
              id={`acquisition-channel-name-${channel.id}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="h-9 max-w-xs"
            />
          </div>
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium">{channel.name}</p>
            {channel.isSystem ? (
              <Badge variant="secondary" className="text-[10px]">
                System
              </Badge>
            ) : null}
            {!channel.isActive ? (
              <Badge variant="outline" className="text-[10px]">
                Archived
              </Badge>
            ) : null}
          </div>
        )}

        <div className="flex shrink-0 items-center gap-2">
          {!canConfigure ? null : channel.isProtected ? (
            // No rename, no archive: the API refuses both with a 400, and
            // offering the control anyway would teach the tenant that the
            // screen lies.
            <p className="text-xs text-muted-foreground">
              The system writes to this channel by slug, so it cannot be renamed or archived.
            </p>
          ) : renaming ? (
            <>
              <Button
                type="button"
                size="sm"
                onClick={() => void saveRename()}
                disabled={!name.trim() || updateChannel.isPending}
              >
                {updateChannel.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setName(channel.name);
                  setRenaming(false);
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={startRename}
                aria-label={`Rename ${channel.name}`}
              >
                Rename
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void toggleArchived()}
                disabled={updateChannel.isPending}
                aria-label={
                  channel.isActive ? `Archive ${channel.name}` : `Restore ${channel.name}`
                }
              >
                {channel.isActive ? 'Archive' : 'Restore'}
              </Button>
            </>
          )}
        </div>
      </div>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * `canConfigure` mirrors the sibling sections on Settings → Leads: writing to
 * the channel list is `leads:configure`, so a viewer sees the vocabulary but is
 * offered no control the API would refuse. It defaults to true so the component
 * is usable on its own; the route passes the real permission.
 */
export function AcquisitionChannelSettingsSection({
  canConfigure = true,
}: {
  canConfigure?: boolean;
} = {}) {
  const channelsQuery = useAcquisitionChannels();
  const createChannel = useCreateAcquisitionChannel();
  const [name, setName] = useState('');
  const [similar, setSimilar] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Server order, reproduced: sortOrder then name. Sorting differently here
  // would reshuffle the list the moment a mutation invalidates the query.
  const channels = useMemo(
    () =>
      [...(channelsQuery.data ?? [])].sort(
        (left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name),
      ),
    [channelsQuery.data],
  );

  async function submit(confirmSimilar: boolean) {
    const trimmed = name.trim();
    if (!canConfigure || !trimmed) return;

    try {
      await createChannel.mutateAsync(
        confirmSimilar ? { name: trimmed, confirmSimilar: true } : { name: trimmed },
      );
      setName('');
      setSimilar(null);
      setError(null);
    } catch (caught) {
      const matches = similarNamesFrom(caught);
      if (matches) {
        setSimilar(matches);
        setError(null);
        return;
      }
      setSimilar(null);
      setError(messageFrom(caught, 'That channel could not be created.'));
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Acquisition Channels</CardTitle>
        <CardDescription>
          Where your work comes from. Archiving a channel takes it out of the pickers rather than
          deleting it, so leads and reports that already reference it keep reading correctly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {channelsQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        ) : channels.length > 0 ? (
          <div className="space-y-2">
            {channels.map((channel) => (
              <ChannelRow key={channel.id} channel={channel} canConfigure={canConfigure} />
            ))}
          </div>
        ) : (
          <Alert>
            <AlertDescription>No acquisition channels yet.</AlertDescription>
          </Alert>
        )}

        {canConfigure ? (
          <form
            className="flex flex-wrap items-end gap-3 rounded-md border border-dashed p-3"
            onSubmit={handleSubmit}
          >
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="acquisition-channel-new-name">Channel name</Label>
              <Input
                id="acquisition-channel-new-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  // The question was about the name that was typed. Editing it
                  // makes the old answer meaningless.
                  setSimilar(null);
                  setError(null);
                }}
                placeholder="Radio ad"
                className="h-9"
              />
            </div>
            <Button type="submit" disabled={!name.trim() || createChannel.isPending}>
              {createChannel.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add channel
            </Button>
          </form>
        ) : null}

        {/* Only `submit` sets these, and it is gated on canConfigure, so a
            viewer can never reach them. */}
        {similar ? (
          <Alert>
            <AlertDescription className="space-y-2">
              <p>You already have something like this:</p>
              <ul className="list-disc space-y-1 pl-5">
                {similar.map((match) => (
                  <li key={match} className="font-medium">
                    {match}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button type="button" size="sm" onClick={() => void submit(true)}>
                  Create it anyway
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setSimilar(null)}>
                  Keep editing
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </CardContent>
    </Card>
  );
}
